/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { Dimmer, Loader, Button, Segment, TextArea } from "semantic-ui-react";
import { selectCurrentUserApiToken, selectCurrentUserId, selectCurrentUserName } from "../slices/currentUser/currentUserSlice";
import { selectChangedResourcesListStatus } from "../slices/changedResourcesList/changedResourcesListSlice";
import { fetchResourceCollection, selectResourceById, inviteCodeUpdated, resourceUpdated } from "../slices/resourceCollection/resourceCollectionSlice";
import NavigationSegment from "./NavigationSegment";
import SimpleResourceFieldViewer from "./SimpleResourceFieldViewer";
import { editModeOn, selectEditMode } from "../slices/editMode/editModeSlice";
import { changeTabFocus, selectTabFocus } from "../slices/tabPane/tabPaneSlice";
import { selectAllResourceChanges } from "../slices/resourceChangeReport/resourceChangeReportSlice";
import { selectOlderVersionFhirString, selectViewingOlderVersion } from "../slices/viewingOlderVersion/viewingOlderVersionSlice";
import { changeTool } from "../slices/footer/footerSlice";
import { selectTriggerRefresh, triggerRefreshOff } from "../slices/triggerRefresh/triggerRefreshSlice";
import { selectGlobalLoading } from "../slices/globalLoading/globalLoadingSlice";
import { changeFoiFocus, selectFoiFocus } from "../slices/foiOfFocus/foiOfFocusSlice";
import { changeTabMenu } from "../slices/tabPane/tabPaneSlice";
import { loadTopSections } from "../slices/topSections/topSectionsSlice";
import getFoiFromFli from "../functions/getFoiFromFliAPI";
import revokeInviteLink from "../functions/revokeInviteLinkAPI";
import getFhirJson from "../functions/getFhirJsonAPI";
import createInviteLink from '../functions/createInviteLinkAPI';
import ListDisplay from "./ListResourceDisplay";
import ResourceBottomDisplay from "./ResourceBottomDisplay";
import ActivityDefinitionDisplay from "./ActivityDefinitionResourceDisplay";
import DocumentReferenceDisplay from "./DocumentReferenceResourceDisplay";
import PlanDefinitionDisplay from "./PlanDefinitionResourceDisplay";
import MeasureDisplay from "./MeasureResourceDisplay";
import LibraryDisplay from "./LibraryResourceDisplay";
import ResearchStudyDisplay from "./ResearchStudyResourceDisplay";
import EvidenceVariableDisplay from "./EvidenceVariableResourceDisplay";
import GroupDisplay from "./GroupResourceDisplay";
import EvidenceDisplay from "./EvidenceResourceDisplay";
import EditUpdateButton from "./resourceDisplayCommonComponents/EditUpdateButton";
import CloneButton from "./resourceDisplayCommonComponents/CloneButton";
import CreateChangeReportButton from "./resourceDisplayCommonComponents/CreateChangeReportButton";
import PublishButton from "./resourceDisplayCommonComponents/PublishButton";
import JSONViewResourceButtons from "./resourceDisplayCommonComponents/JSONViewResourceButtons";
import ResourceStatusRadioButton from "./resourceDisplayCommonComponents/ResourceStatusRadioButton";
import ViewTabPane from "./resourceDisplayCommonComponents/ViewTabPane";
import FeedbackButton from '../components/FeedbackButton';
import ParentResourceAlertModal from './ParentResourceAlertModal'
import ViewEditJSONButton from "./resourceDisplayCommonComponents/ViewEditJSONButton";
import TermDetailLeftSideDisplay from "./TermDetailLeftSideDisplay";
import BundleDisplay from "./BundleResourceDisplay";
import ValueSetDisplay from "./ValueSetResourceDisplay";
import CitationDisplay from "./CitationResourceDisplay";
import CompositionDisplay from "./CompositionResourceDisplay";
import ArtifactAssessmentDisplay from "./ArtifactAssessmentResourceDisplay";
import CodeSystemDisplay from "./CodeSystemResourceDisplay";
import CodeSystemTermEdit from "./CodeSystemTermEdit";
import CodeSystemTermDisplay from "./CodeSystemTermDisplay";
import QuestionnaireDisplay from "./QuestionnaireResourceDisplay";
import ImplementationGuideDisplay from "./ImplementationGuideResourceDisplay";
import getDisplayTitle from "./resourceDisplayCommonFunctions/getDisplayTitle";
import { VersionDifferenceViewer } from "./VersionDifferencesFunctions";
import { simpleDisplayStringFromFHIRCodeableConcept } from "../datatypeComponents/CodeableConceptEntry";
import { displayStringFromFHIRReference } from "../datatypeComponents/ReferenceEntry";
import ResearchSubjectDisplay from "./ResearchSubjectResourceDisplay";
import scrollTo from '../functions/scrollTo';
import { changeHasApprovalProperty } from "../slices/codeSystemTermDetail/codeSystemTermDetailSlice";
import navigateToAnotherTerm from "./codeSystemResourceFunctions/navigateToAnotherTerm";

const ResourceTextDisplay = ({ resourceType, resourceId, fhirJson }) => {
    const dispatch = useDispatch();
    const viewingOlderVersion = useSelector(state => selectViewingOlderVersion(state));
    const olderVersionFhirString = useSelector(state => selectOlderVersionFhirString(state));

    if (typeof (fhirJson) === "object" && fhirJson?.resourceType === resourceType) {
        try {
            switch (resourceType) {
                case "ActivityDefinition":
                    return <ActivityDefinitionDisplay foi={resourceId} />;
                case "ArtifactAssessment":
                    return <ArtifactAssessmentDisplay foi={resourceId} />;
                case "Citation":
                    return <CitationDisplay foi={resourceId} />;
                case "CodeSystem":
                    return <CodeSystemDisplay foi={resourceId} />;
                case "Composition":
                    return <CompositionDisplay foi={resourceId} />;
                case "DocumentReference":
                    return <DocumentReferenceDisplay foi={resourceId} />; //TODO - this may not be reachable from higher routing
                case "Evidence":
                    return <EvidenceDisplay foi={resourceId} />;
                case "EvidenceVariable":
                    return <EvidenceVariableDisplay foi={resourceId} />;
                case "Group":
                    return <GroupDisplay foi={resourceId} />;
                case "ImplementationGuide":
                    return <ImplementationGuideDisplay foi={resourceId} />;
                case "Library":
                    return <LibraryDisplay foi={resourceId} />;
                case "List":
                    return <ListDisplay foi={resourceId} />;
                case "Measure":
                    return <MeasureDisplay foi={resourceId} />;
                case "PlanDefinition":
                    return <PlanDefinitionDisplay foi={resourceId} />;
                case "Questionnaire":
                    return <QuestionnaireDisplay foi={resourceId} />;
                case "ResearchStudy":
                    return <ResearchStudyDisplay foi={resourceId} />;
                case "ResearchSubject":
                    return <ResearchSubjectDisplay foi={resourceId} />;
                case "ValueSet":
                    return <ValueSetDisplay foi={resourceId} />;
                default:
                    dispatch(changeTool("FEvIR®: Resource Viewer"));
                    return <div style={{ marginTop: "12px" }}>
                        <SimpleResourceFieldViewer resource={(viewingOlderVersion && olderVersionFhirString) ?
                            olderVersionFhirString : fhirJson}
                            parentElement={""} startExpanded={true} />
                    </div>
            }
        } catch (e) {
            return <>Invalid {resourceType} JSON. See JSON Tab to see the resource.</>
        }
    } else {
        return <></>
    }
}

const CantDisplayResource = ({ foi }) => {
    const userId = useSelector(selectCurrentUserId);
    const parentResourceDetail = useSelector(state => selectResourceById(state, foi));
    const resourceJson = parentResourceDetail?.revisedResourceJson || parentResourceDetail?.resourceJson;
    let displayMessage = "";
    if (resourceJson?.resourceType) { //This doesn't make much sense to me, we shouldn't be able to know the resourceType, but would have to trace it.
        if (userId) {
            displayMessage = "You do not have permission to view this resource.";
        } else {
            displayMessage = "Please log in to view resource.";
        }
    } else {
        displayMessage = "Resource not found";
    }
    return <span><h1>{displayMessage}<br /><br /></h1></span>
};

const addTermToLinkDictionary = (parentJson, parentPath, parentPathIndexes, parentPathString,
    termToLinkDictionary, dispatch, navigate, resourceId) => {
    let conceptIndex = 0;
    for (const concept of parentJson.concept) {
        if (concept.code) {
            let display = concept.display;
            let path = [...parentPath, display];
            let pathIndexes = [...parentPathIndexes, conceptIndex];
            let pathString = parentPathString ? parentPathString + ": " + display : display;
            const linkOnClick = () => {
                navigateToAnotherTerm(dispatch, resourceId,
                    concept, display, path, pathIndexes, pathString, navigate);
            }
            termToLinkDictionary[concept.code] = linkOnClick;
            if (concept.concept) {
                addTermToLinkDictionary(concept, path, pathIndexes, pathString, termToLinkDictionary, dispatch, navigate, resourceId);
            }
        }
        conceptIndex += 1;
    }
}

const createTermToLinkDictionary = (resourceJson, dispatch, navigate, resourceId) => {
    let termToLinkDictionary = {};
    addTermToLinkDictionary(resourceJson, [], [], "", termToLinkDictionary, dispatch, navigate, resourceId);
    return termToLinkDictionary;
};

// eslint-disable-next-line no-empty-pattern
const ResourceDisplay = ({  }) => {
    const foi = useSelector(selectFoiFocus);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    //const triggerRefresh = useSelector(selectTriggerRefresh);
    const apiToken = useSelector(selectCurrentUserApiToken);
    const userName = useSelector(selectCurrentUserName);
    const userId = useSelector(selectCurrentUserId);
    const siteIsLoading = useSelector(selectGlobalLoading);

    const editMode = useSelector(state => selectEditMode(state));
    const tabFocus = useSelector(state => selectTabFocus(state));

    const parentResourceDetail = useSelector(state => selectResourceById(state, foi));
    const resourceJson = parentResourceDetail?.revisedResourceJson || parentResourceDetail?.resourceJson;
    const resourceChanges = useSelector(state => selectAllResourceChanges(state));
    const [statusChanged, setStatusChanged] = useState(false);

    const editPermission = parentResourceDetail?.usageData?.editpermission;
    const readPermission = parentResourceDetail?.usageData?.readpermission;
    const adminPermission = parentResourceDetail?.usageData?.adminpermission;
    const inviteCode = parentResourceDetail?.usageData?.invitecode;
    const editors = parentResourceDetail?.usageData?.editors;
    const pubStatus = parentResourceDetail?.usageData?.status;
    const viewingOlderVersion = useSelector(state => selectViewingOlderVersion(state));
    const olderVersionFhirString = useSelector(state => selectOlderVersionFhirString(state));
    let [termToLinkDict, setTermToLinkDict] = useState(null);

    const [jsonViewEditingState, setJsonViewEditingState] = useState({
        "fhirEntryJson": {}, "fhirEntryString": "", "fhirEntryStringRevised": "",
        "fhirEntryStringChange": false,
        "status": "active"
    });
    const [creatingInviteState, setCreatingInviteState] = useState(false);
    const [parentResourceAlertModalState, setParentResourceAlertModalState] = useState({ modalOpen: false, parentResourcesToBeAlertedTo: undefined });

    //TODO, a way to minimize the left side panel. This code can be implemented for the other navigation pages as well like term detail
    const [sidebarOpen, setSidebarOpen] = useState(true); //This can be later done at the global level and preferences remembered for the user locally or on the userstable in the database

    //useEffect(() => { }, [triggerRefresh]);

    const navigateToHashTerm = () => {
        let hashCode = location.hash?.substring(1);
        if (hashCode && // !hashLoaded &&
            (resourceJson?.resourceType === "CodeSystem" || resourceJson?.resourceType === "ArtifactAssessment") &&
            location.hash?.length > 1 && termToLinkDict[hashCode]) { //And maybe a check to make sure the current term code isn't the same as the one it's currently on
            // setHashLoaded(true);
            termToLinkDict[hashCode]();
            scrollTo("navigationTermDetailSegment", "termdetail-navigation-code-" + hashCode, true);
        }
    }

    useEffect(() => {
        navigateToHashTerm();
    }, [termToLinkDict, location.hash]);

    useEffect(() => {
        if (resourceJson?.resourceType) {
            let resourceType = resourceJson.resourceType;
            if (resourceType === "CodeSystem") {
                dispatch(changeTabMenu(["Text View", "Term Detail", "JSON View", "Usage View"]));
            } else {
                dispatch(changeTabMenu(["Text View", "JSON View", "Usage View"]));
            }
        }
    }, [resourceJson?.resourceType]);

    useEffect(() => {
        //console.log("ResourceDisplay useEffect due to change in resourceJson")
        if (resourceJson) {
            setJsonViewEditingState({
                "fhirEntryJson": resourceJson,
                "fhirEntryString": JSON.stringify(resourceJson),
                "fhirEntryStringRevised": "",
                "fhirEntryStringChange": false,
                "status": pubStatus || "active"
            });
            if (resourceJson.resourceType === "CodeSystem") {
                let hasApprovalProperty = false;
                if (resourceJson.property) {
                    for (const property of resourceJson.property) {
                        if (property.code === "approval") {
                            hasApprovalProperty = true;
                        }
                    }
                }
                if (location.hash) {
                    dispatch(changeTabFocus("Term Detail"));
                }
                dispatch(changeHasApprovalProperty(hasApprovalProperty));
                if (!termToLinkDict) {
                    let termToLinkDictionary = createTermToLinkDictionary(resourceJson, dispatch, navigate, foi);
                    setTermToLinkDict(termToLinkDictionary);
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resourceJson]);

    let inviteText = "";

    if (inviteCode) {
        inviteText = `You're invited by ${userName} to become an editor for the ${parentResourceDetail.displayTitle} resource FOI ${foi}\nYou can accept this invite by going to this URL, be logged in, and then click the Accept button.\nhttps://fevir.net/invite/${inviteCode}`;
    }

    return <div>
        <div style={{ display: "flex", width: "100%" }}>
            <div>
                {(tabFocus === "Text View") &&
                    <div className="left-sidebar-container-parent" style={{ float: "left", minWidth: "290px", marginRight: "18px" }}>
                        {sidebarOpen ?
                            <div className="left-sidebar-container" style={{ position: "absolute", width: "290px" }}>
                                <h3 style={{ fontSize: "1.1rem", marginTop: "0px", marginBottom: "0px", textAlign: "center" }}>
                                    {resourceJson ?
                                        getDisplayTitle(resourceJson, parentResourceDetail)
                                        :
                                        <>&nbsp;</>
                                    }
                                </h3>
                                <NavigationSegment resourceId={resourceJson?.id}
                                    editor={editPermission} />
                                <div />
                                <div>
                                    {!viewingOlderVersion &&
                                        <>
                                            <div style={{ width: "100%", textAlign: "center", display: "flex" }}>
                                                <EditUpdateButton foi={foi}
                                                    jsonViewEditingState={jsonViewEditingState}
                                                    setJsonViewEditingState={setJsonViewEditingState}
                                                    setParentResourceAlertModalState={setParentResourceAlertModalState} />
                                            </div>
                                        </>
                                    }
                                    <div style={{ marginTop: "10px" }} />
                                    <div style={{ width: "100%", textAlign: "center", display: "flex" }}>
                                        <div style={{ width: "50%", textAlign: "left", float: "left" }}>
                                            <ViewEditJSONButton />
                                        </div>
                                        <div style={{ width: "50%", float: "right", textAlign: "right" }}>
                                            {
                                                editMode || resourceChanges.length > 0
                                                    ?
                                                    <CreateChangeReportButton foi={foi} />
                                                    :
                                                    <CloneButton foi={foi} />
                                            }
                                        </div>
                                    </div>
                                    {(!editMode && resourceJson && (resourceJson.status === "draft" ||
                                        resourceJson.status === "preliminary") && editPermission) &&
                                        <div style={{ width: "100%", textAlign: "center", display: "flex" }}>
                                            <div style={{ width: "100%", float: "left", textAlign: "left" }}>
                                                <div style={{ marginTop: "10px" }} />
                                                <PublishButton foi={foi} additionalClassName={"navigationButton"}
                                                    jsonViewEditingState={jsonViewEditingState}
                                                    setJsonViewEditingState={setJsonViewEditingState} />
                                            </div>
                                        </div>
                                    }
                                </div>
                            </div>
                            :
                            <div className="minimal-left-sidebar" style={{ position: "absolute", width: "20px" }}>
                                Basically just an expand left side panel button and a save icon with a floppy disk, this mode is useful for extra-wide content, smaller screens, and screenshots.
                            </div>
                        }
                    </div>
                }
                {tabFocus === "Term Detail" &&
                    <div>
                        <TermDetailLeftSideDisplay foi={foi} termToLinkDict={termToLinkDict}
                            setTermToLinkDict={setTermToLinkDict} />
                    </div>
                }
                {tabFocus === "Comparison Detail" &&
                    <>
                        {/*
                <ComparisonDetailLeftSideDisplay loading={loading} setLoadingSubmit={setLoadingSubmit}
                    fhirEntryState={fhirEntryState} setFhirEntryState={setFhirEntryState} formState={formState}
                    resourceId={resourceId} resourceType={resourceType} formInputsStateRef={formInputsStateRef}
                    changeFhirEntryState={changeFhirEntryState} submitUpdatedFhirResource={submitUpdatedFhirResource}
                    changeFormState={changeFormState} addToast={addToast} globalContext={globalContext}
                    history={history} setPageEditModeState={setPageEditModeState} />
                        */}
                    </>
                }
            </div>
            <div style={{ float: "right", width: "100%" }}>
                <Segment id="resourceTextViewSegment" name="resourceTextViewSegment"
                    className={`containerSegment remainderOfPageSegment ${editMode && "editSegment"} ${viewingOlderVersion && "historySegment"}`}
                    raised>
                    <ViewTabPane foi={foi} jsonViewEditingState={jsonViewEditingState}
                        setJsonViewEditingState={setJsonViewEditingState}
                        statusChanged={statusChanged} />
                    {(!siteIsLoading && readPermission === false && editPermission === false) &&
                        <CantDisplayResource foi={foi} />
                    }
                    <div style={{ textAlign: "left" }}>
                        {tabFocus === "Text View" &&
                            <>
                                {parentResourceAlertModalState.modalOpen &&
                                    <ParentResourceAlertModal
                                        parentResourceAlertModalState={parentResourceAlertModalState}
                                        setParentResourceAlertModalState={setParentResourceAlertModalState} />}
                                {resourceJson ?
                                    <>{(readPermission || editPermission) &&
                                        <>
                                            {(resourceJson?.resourceType && resourceJson.resourceType !== "") ?
                                                <>
                                                    {resourceJson.resourceType === "Bundle" ?
                                                        <>
                                                            <BundleDisplay foi={resourceJson.id} />
                                                        </>
                                                        :
                                                        <>
                                                            {resourceJson.resourceType === "DocumentReference" ?
                                                                <DocumentReferenceDisplay foi={resourceJson.id} />
                                                                :
                                                                <>
                                                                    <ResourceTextDisplay
                                                                        resourceType={resourceJson.resourceType}
                                                                        resourceId={resourceJson.id}
                                                                        fhirJson={resourceJson}
                                                                    />
                                                                    <br />  <br />
                                                                    <ResourceBottomDisplay fhirJson={resourceJson} />
                                                                </>
                                                            }
                                                        </>
                                                    }
                                                </>
                                                :
                                                <>{!siteIsLoading &&
                                                    <Dimmer className={"loadingDimmer"} active inverted>
                                                        <Loader inverted>LoadingZZZ</Loader>
                                                    </Dimmer>
                                                }</>
                                            }
                                        </>
                                    }
                                    </>
                                    :
                                    <Dimmer className={"loadingDimmer"} active inverted>
                                        <Loader inverted>Loading...</Loader>
                                    </Dimmer>
                                }
                            </>
                        }
                        {tabFocus === "Term Detail" &&
                            <>{(readPermission || editPermission) &&
                                <>{editMode ?
                                    <CodeSystemTermEdit resourceId={foi} />
                                    :
                                    <CodeSystemTermDisplay resourceId={foi} />
                                }
                                </>
                            }</>
                        }
                        {tabFocus === "Comparison Detail" &&
                            <><span>TODO</span></>
                        }
                        {tabFocus === "Section Detail" &&
                            <><span>TODO</span>{/**
                    <SectionDetail formInputsStateRef={formInputsStateRef} resourceId={resourceId}
                        resourceState={resourceState} setResourceState={setResourceState}
                        fhirEntryState={fhirEntryState} setFhirEntryState={setFhirEntryState} />
                                */}</>
                        }
                        {tabFocus === "JSON View" &&
                            <>
                                {parentResourceAlertModalState.modalOpen &&
                                    <ParentResourceAlertModal
                                        parentResourceAlertModalState={parentResourceAlertModalState}
                                        setParentResourceAlertModalState={setParentResourceAlertModalState} />}
                                {(resourceJson?.resourceType && (readPermission || editPermission)) &&
                                    <>
                                        <h2 style={{ marginTop: "6px", marginBottom: "12px" }}>
                                            {getDisplayTitle(resourceJson, parentResourceDetail)}
                                        </h2>
                                        <TextArea style={{ minHeight: "400px", width: "65%" }}
                                            value={olderVersionFhirString || jsonViewEditingState.fhirEntryStringRevised ||
                                                (jsonViewEditingState.fhirEntryString &&
                                                    JSON.stringify(JSON.parse(jsonViewEditingState.fhirEntryString), null, 2)) || ''}
                                            onChange={(e) => {
                                                setJsonViewEditingState(prevState => {
                                                    return {
                                                        ...prevState,
                                                        "fhirEntryStringRevised": e.target.value,
                                                        "fhirEntryStringChange": true
                                                    }
                                                })
                                            }}
                                            readOnly={!editMode || viewingOlderVersion} />
                                        <br /><br />
                                        {editMode &&
                                            <ResourceStatusRadioButton foi={foi}
                                                setJsonViewEditingState={setJsonViewEditingState}
                                                setStatusChanged={setStatusChanged} />}
                                    </>
                                }
                                <JSONViewResourceButtons foi={foi}
                                    jsonViewEditingState={jsonViewEditingState}
                                    setJsonViewEditingState={setJsonViewEditingState}
                                    setParentResourceAlertModalState={setParentResourceAlertModalState}
                                    statusChanged={statusChanged} />
                                <br /><br /><br />
                            </>
                        }
                        {tabFocus === "Usage View" &&
                            <>
                                {(parentResourceDetail?.usageData && (readPermission || editPermission)) &&
                                    <>
                                        <h2 style={{ marginTop: "6px", marginBottom: "12px" }}>{parentResourceDetail.displayTitle}</h2>
                                        <br />
                                        <p>This resource has been accessed <b>{parentResourceDetail.usageData.totalviews}</b> times by at least <b>{parentResourceDetail.usageData.totalusersviewed}</b> users.</p>
                                        {(parentResourceDetail.usageData.totaledits > 0) && <p>This resource has been modified <b>{parentResourceDetail.usageData.totaledits}</b> times by at least <b>{parentResourceDetail.usageData.totalusersedited}</b> users.</p>}
                                        <br />
                                        {(resourceJson?.meta.versionId > 1) && <>
                                            <VersionDifferenceViewer foi={resourceJson.id} />
                                        </>
                                        }
                                        {adminPermission &&
                                            <>
                                                <div className="resourceListHeader" style={{ cursor: "default" }}>
                                                    <div style={{ paddingTop: "20px" }}
                                                        className="resourceListHeaderText">
                                                        <span>Co-editors</span>
                                                    </div>
                                                </div>
                                                {editors?.map((editor, editorIndex) => {
                                                    return <h4 key={editorIndex} style={{ margin: "0px" }}>{editor.name}</h4>
                                                })}
                                                <br />
                                                {inviteCode ?
                                                    <>
                                                        <div style={{
                                                            width: "100%", padding: "6px", background: "#FFFFFF",
                                                            border: "1px solid #000000", whiteSpace: "pre-wrap"
                                                        }}>{inviteText}</div>
                                                        <br />
                                                        <Button className="formButton" style={{ color: "#000000" }}
                                                            content="Copy Invite to Clipboard"
                                                            onClick={() => { navigator.clipboard.writeText(inviteText) }} />
                                                        <br /><br />
                                                        <Button className="formButton" style={{ color: "#000000" }}
                                                            content="Disable Invite-to-Edit Link"
                                                            onClick={async () => {
                                                                if (window.confirm("Are you sure you want to disable this invite link to no longer add additional co-editors to this resource?")) {
                                                                    let response = await revokeInviteLink(resourceJson.id, inviteCode, apiToken);
                                                                    if (response?.success) {
                                                                        let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
                                                                        newResourceDetail.usageData.invitecode = null;
                                                                        dispatch(resourceUpdated(newResourceDetail));
                                                                    } else {
                                                                        alert("Trouble revoking invite code");
                                                                    }
                                                                }
                                                            }}
                                                            disabled={viewingOlderVersion}
                                                        />
                                                    </>
                                                    :
                                                    <>
                                                        <Button className="formButton" style={{ color: "#000000" }}
                                                            content="Create Invite-to-Edit Link"
                                                            onClick={async () => {
                                                                setCreatingInviteState(true);
                                                                let inviteCodeRetrieved = await createInviteLink(foi, apiToken);
                                                                dispatch(inviteCodeUpdated({ id: foi, inviteCode: inviteCodeRetrieved }));
                                                                setCreatingInviteState(false);
                                                            }}
                                                            disabled={creatingInviteState || viewingOlderVersion} />
                                                    </>
                                                }
                                            </>
                                        }
                                        <br /><br /><br />
                                    </>
                                }
                            </>
                        }
                    </div>
                </Segment>
            </div >
        </div >
    </div >
}

const navigateByFli = async (fli, apiToken, navigate, location, dispatch, fliAttempt = false) => {
    //dispatch(defaultToResourceViewer());
    if (fli === "SEVCO") {
        fli = "sevco";
    }
    let response = await getFoiFromFli(fli, apiToken);
    if (response?.success && response.formstateid) {
        //navigate(0);
        dispatch(changeFoiFocus(response.formstateid.toString()));
        navigate("/resources/" + response.resourcetype + "/" + response.formstateid + location.search + location.hash, { replace: true });
    } else if (fliAttempt) {
        //TODO: Set the fli to a selector and have it auto-fill the text box
        navigate("/FLI", { replace: true });
    } else {
        navigate("/pagenotfound");
    }
}

// eslint-disable-next-line no-empty-pattern
const ResourceDisplayPage = ({ fliAttempt, editmode }) => {
    const { foi } = useParams();
    const [urlEditMode, setUrlEditMode] = useState(editmode);
    const dispatch = useDispatch();
    const apiToken = useSelector(selectCurrentUserApiToken);
    const userId = useSelector(selectCurrentUserId);
    const changesSaved = useSelector(selectChangedResourcesListStatus);
    const navigate = useNavigate();
    const location = useLocation();
    const triggerRefresh = useSelector(selectTriggerRefresh);
    const foiFocus = useSelector(selectFoiFocus);
    const parentResourceDetail = useSelector(state => selectResourceById(state, foiFocus));
    const editPermission = parentResourceDetail?.usageData?.editpermission;
    const resourceJson = parentResourceDetail?.revisedResourceJson || parentResourceDetail?.resourceJson;
    const loadTheFhirJson = async (FOI, userId = null) => {
        //dispatch(globalLoadingOn());
        //dispatch(defaultToResourceViewer());
        //console.log("loadTheFhirJson")
        let fhirJson = await getFhirJson(FOI, null, apiToken);
        if (fhirJson && typeof fhirJson === "object" && fhirJson.id) { //Added fhirJson.id in this condition on November 18, 2025
            const resourceType = fhirJson.resourceType;
            const resourceUrlPath = `/resources/${resourceType}/${FOI || fhirJson.id}`;
            if (window.location.pathname !== resourceUrlPath) {
                if (location.hash === "#json") {
                    navigate(resourceUrlPath + location.search, { replace: true });
                    dispatch(changeTabFocus("JSON View"));
                } else {
                    navigate(resourceUrlPath + location.search + location.hash, { replace: true });
                }
            } else {
                if (location.hash === "#json") {
                    navigate(resourceUrlPath + location.search, { replace: true });
                    dispatch(changeTabFocus("JSON View"));
                }
            }
            //dispatch(globalLoadingOff());
            let productFamily = resourceType;
            if (resourceType === "Composition") {
                productFamily = fhirJson.type?.coding?.[0]?.code || fhirJson.type?.text || "Composition";
                if (fhirJson.section?.length > 0) {
                    let topSectionArray = fhirJson.section.map((section, sectionIndex) => {
                        return {
                            id: sectionIndex,
                            displayTitle: section?.title ||
                                simpleDisplayStringFromFHIRCodeableConcept(section?.code) ||
                                displayStringFromFHIRReference(section?.focus) || "Untitled Section",
                            compositionId: FOI,
                            productFamily: productFamily,
                            initialSection: section,
                            revisedSection: section
                        };
                        //if (section) { }
                        //else { return null } //TO DO //https://fevir.net/resources/Composition/156056
                    });
                    let topSectionsBody = { topSections: topSectionArray };
                    dispatch(loadTopSections(topSectionsBody));
                }
            }
            let body = {
                fhirJson: fhirJson
            };
            dispatch(fetchResourceCollection(body));
        } else if (fhirJson === null) { //Don't have permission to view the resource
            if (userId === "1") {
                alert("Please log in to view resource.");
                navigate("/search"); //This is different from how it's on current FEvIR, you'd be still on the resource viewer page, for this one especially I think it's better to stay on the page
            } else {
                alert("You do not have permission to view this resource.");
                navigate("/search"); //This is different from how it's on current FEvIR, you'd be still on the resource viewer page
            }
        } else if (fhirJson && !fhirJson.id) {
            alert('This Resource doesn\'t have an "id" element in the JSON.');
            navigate("/search");
        } else {
            alert("No Resource found for that FOI.");
            //navigate("/");  //Would navigate(0); make more sense? //This isn't needed I believe
            navigate("/search"); //This is different from how it's on current FEvIR, on current FEvIR it's the resource page with "Resource not found"
        }
    };

    useEffect(() => {
        if (foi) {
            if (!isNaN(foi)) {
                dispatch(changeFoiFocus(foi));
            } else {
                navigateByFli(foi, apiToken, navigate, location, dispatch, fliAttempt);
            }
        }
    }, []);

    useEffect(() => {
        if (urlEditMode && editPermission) {
            dispatch(editModeOn());
            setUrlEditMode(false);
            //navigate("/" + foi, { replace: true });
        }
    }, [editPermission]);

    useEffect(() => { //For when you navigate to a page perhaps via back button and doesn't properly change FOI focus, a catch all
        if (resourceJson && !isNaN(foi) && foi !== foiFocus.toString()) { // && foi !== resourceJson.id
            dispatch(changeFoiFocus(foi));
        }
    }, [foi]);
    /*
        useEffect(() => {
            console.log("ResourceDisplayPage useEffect due to change in resourceJson")
         }, [resourceJson]);
    */
    useEffect(() => {
        //console.log("ResourceDisplayPage useEffect due to change in triggerRefresh = " + triggerRefresh);
        if (triggerRefresh === true && foiFocus && !isNaN(foiFocus)) {
            loadTheFhirJson(foiFocus, userId);
            dispatch(triggerRefreshOff());
        }
    }, [triggerRefresh]);

    useEffect(() => {
        //console.log("ResourceDisplayPage useEffect due to change in changesSaved = " + changesSaved)
        if (userId && changesSaved === "empty" && foiFocus && !isNaN(foiFocus)) {
            loadTheFhirJson(foiFocus, userId);
        }
    }, [changesSaved, userId, foiFocus]);

    return <>
        <FeedbackButton className="feedbackButton" />
        {/*<Button content="Test" onClick={() => { dispatch(changeFoiFocus(400012)); navigate("/resources/Composition/400012"); }} />*/}
        {
            (apiToken || userId === "1") &&
            <ResourceDisplay />
        }
    </>
}

export default ResourceDisplayPage